import { useState } from 'react';
import { radioLives, type RadioLive } from '@/mocks/radioContent';
import { radioStations } from '@/mocks/radios';

export default function LiveTab() {
  const [filter, setFilter] = useState<'todos' | 'en_vivo' | 'programado' | 'finalizado'>('todos');

  const filtered = radioLives.filter((l) =>
    filter === 'todos' ? true : l.status === filter,
  );

  const sorted = [...filtered].sort((a, b) => {
    const order: Record<string, number> = { en_vivo: 0, programado: 1, finalizado: 2 };
    return order[a.status] - order[b.status];
  });

  return (
    <div className="flex flex-col h-full bg-background-50">
      {/* Header */}
      <div className="safe-top px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground-950">Live</h1>
            <p className="text-foreground-500 text-sm mt-0.5">Transmisiones en vivo</p>
          </div>
          {/* Live count */}
          {radioLives.filter((l) => l.status === 'en_vivo').length > 0 && (
            <div className="flex items-center gap-1.5 bg-accent-500/10 px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></span>
              <span className="text-accent-600 text-xs font-bold">
                {radioLives.filter((l) => l.status === 'en_vivo').length} en vivo
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="px-4 pb-4 overflow-x-auto scrollbar-none">
        <div className="flex gap-2">
          {[
            { key: 'todos', label: 'Todos' },
            { key: 'en_vivo', label: 'En Vivo' },
            { key: 'programado', label: 'Próximos' },
            { key: 'finalizado', label: 'Finalizados' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key as typeof filter)}
              className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                filter === f.key
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Live list */}
      <div className="flex-1 overflow-y-auto px-4 pb-24 scrollbar-none">
        <div className="space-y-3">
          {sorted.map((live, idx) => {
            const station = radioStations.find((s) => s.id === live.stationId);
            return (
              <div
                key={live.id}
                className="bg-background-100 rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
                style={{
                  animation: `fadeInUp 0.4s ease-out forwards`,
                  animationDelay: `${idx * 60}ms`,
                  opacity: 0,
                }}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-background-200 overflow-hidden">
                  <img
                    src={live.imageUrl}
                    alt={live.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {/* Status badge */}
                  <div className="absolute top-3 left-3">
                    {live.status === 'en_vivo' && (
                      <span className="flex items-center gap-1.5 bg-accent-500/90 backdrop-blur-sm px-2.5 py-1 rounded-full">
                        <span className="w-1.5 h-1.5 bg-background-50 rounded-full animate-pulse"></span>
                        <span className="text-background-50 text-[10px] font-bold">EN VIVO</span>
                      </span>
                    )}
                    {live.status === 'programado' && (
                      <span className="bg-primary-500/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-background-50 text-[10px] font-bold">
                        PRÓXIMAMENTE
                      </span>
                    )}
                    {live.status === 'finalizado' && (
                      <span className="bg-foreground-900/70 backdrop-blur-sm px-2.5 py-1 rounded-full text-background-50 text-[10px] font-bold">
                        FINALIZADO
                      </span>
                    )}
                  </div>
                  {/* Station tag */}
                  {station && (
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-foreground-950/60 backdrop-blur-sm px-2 py-1 rounded-full">
                      <div className="w-4 h-4 rounded-full overflow-hidden bg-background-300">
                        <img
                          src={station.logoUrl}
                          alt={station.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="text-background-50 text-[10px] font-semibold truncate max-w-[80px]">
                        {station.name}
                      </span>
                    </div>
                  )}
                  {/* Duration */}
                  <div className="absolute bottom-3 left-3 bg-foreground-950/70 backdrop-blur-sm px-2 py-0.5 rounded-md text-background-50 text-[10px] font-medium">
                    {live.duration}
                  </div>
                  {/* Viewers */}
                  {live.status === 'en_vivo' && live.viewers > 0 && (
                    <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-foreground-950/70 backdrop-blur-sm px-2 py-0.5 rounded-md">
                      <i className="ri-eye-line text-background-50 text-[10px]"></i>
                      <span className="text-background-50 text-[10px] font-medium">
                        {live.viewers.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-3.5">
                  <h3 className="text-foreground-950 font-semibold text-sm leading-tight">
                    {live.title}
                  </h3>
                  <p className="text-foreground-500 text-xs mt-1 line-clamp-2">{live.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1 text-foreground-400 text-xs">
                      <i className="ri-user-line text-xs"></i>
                      <span>{live.hostName}</span>
                    </div>
                    <span className="text-foreground-300 text-[10px]">·</span>
                    <span className="text-foreground-400 text-xs">
                      {live.status === 'programado' && live.scheduledDate
                        ? `${live.scheduledDate} ${live.scheduledTime || ''}`
                        : live.date}
                    </span>
                  </div>
                  {/* Reactions */}
                  {live.reactions.length > 0 && (
                    <div className="flex items-center gap-2 mt-2.5">
                      {live.reactions.slice(0, 5).map((r) => (
                        <span
                          key={r.type}
                          className="text-xs bg-background-200 px-2 py-0.5 rounded-full"
                        >
                          {r.type} {r.count}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Comments preview */}
                  {live.comments.length > 0 && (
                    <div className="mt-2.5 pt-2.5 border-t border-background-200">
                      {live.comments.slice(0, 2).map((c) => (
                        <div key={c.id} className="flex items-start gap-2 mb-1.5">
                          <img
                            src={c.avatarUrl}
                            alt={c.userName}
                            className="w-5 h-5 rounded-full object-cover mt-0.5"
                          />
                          <p className="text-foreground-600 text-xs leading-relaxed">
                            <span className="font-semibold text-foreground-800">{c.userName}</span>{' '}
                            {c.text}
                          </p>
                        </div>
                      ))}
                      {live.comments.length > 2 && (
                        <p className="text-foreground-400 text-[10px] mt-1 cursor-pointer hover:text-foreground-600">
                          Ver {live.comments.length - 2} comentarios más
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {sorted.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-live-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">Sin transmisiones</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}